import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableCell } from '@/components/ui/table';
import { ClickableTableRow } from '@/shared/components/ClickableTableRow';
import type { Candidate, CandidateStatus } from '../types/candidate.types';
import { CandidateStatusBadge } from './CandidateStatusBadge';
import { CandidateActions } from './CandidateActions';
import { CandidateNameCell, ConsentCell, CountryCell } from './CandidateRowCells';

export interface CandidateRowProps {
  candidate: Candidate;
  onEdit: (candidate: Candidate) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: CandidateStatus) => void;
  onResendInvitation: (id: string) => void;
  onCopyLink: (shareId: string) => void;
}

export function CandidateRow({
  candidate,
  onEdit,
  onDelete,
  onStatusChange,
  onResendInvitation,
  onCopyLink,
}: CandidateRowProps): JSX.Element {
  const navigate = useNavigate();

  const handleNameClick = (): void => {
    navigate(`/candidates/${candidate.id}`);
  };

  return (
    <ClickableTableRow
      onActivate={handleNameClick}
      label={candidate.name}
      className="group"
      testId={`candidate-row-${candidate.id}`}
    >
      <CandidateNameCell name={candidate.name} email={candidate.email} onClick={handleNameClick} />
      <CountryCell country={candidate.country} />
      <TableCell>
        <CandidateStatusBadge status={candidate.status} />
      </TableCell>
      <ConsentCell consentGiven={candidate.consentGiven} />
      <TableCell className="text-sm text-muted-foreground">
        {new Date(candidate.createdAt).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-right">
        <CandidateActions
          candidate={candidate}
          onEdit={onEdit}
          onDelete={onDelete}
          onStatusChange={onStatusChange}
          onResendInvitation={onResendInvitation}
          onCopyLink={onCopyLink}
        />
      </TableCell>
    </ClickableTableRow>
  );
}
